import type { CompiledScript } from './compiler.js';
import { Driver } from './driver.js';
import type { Bar, BarMagnifierData } from './feed.js';
import { barMagnifierTimeframe } from '../runtime/timeframe.js';
import { ExecutionContext } from '../runtime/context.js';
import type { OutputCollector } from '../runtime/output.js';
import type { DrawObject } from '../runtime/builtins/drawing.js';

export type HistoricalTailRunKind = 'unchanged' | 'suffix' | 'full';

export type HistoricalTailRunReason =
  | 'explicit-seed'
  | 'incremental-state-unavailable'
  | 'incremental-state-poisoned'
  | 'symbol-change'
  | 'timeframe-change'
  | 'inputs-change'
  | 'security-bars-change'
  | 'mintick-change'
  | 'magnifier-data-change'
  | 'unchanged'
  | 'empty'
  | 'append'
  | 'prepend'
  | 'shrink'
  | 'length-change'
  | 'retime'
  | 'historical-change'
  | 'strategy'
  | 'request-security'
  | 'tail-replay-unavailable'
  | 'tail-replay';

export interface HistoricalTailRunResult {
  kind: HistoricalTailRunKind;
  reason: HistoricalTailRunReason;
  dirtyFromIndex: number;
  structural: boolean;
}

export interface HistoricalTailRunContext {
  symbol?: string;
  timeframe?: string;
  inputs?: Readonly<Record<string, unknown>>;
  securityBars?: Readonly<Record<string, readonly Bar[]>>;
  /** Positive finite symbol tick size; invalid values use Engine's 0.01 default. */
  mintick?: number;
  /** Dedicated, validated-at-run-boundary Bar Magnifier data channel. */
  magnifierData?: BarMagnifierData;
}

export interface HistoricalTailRunnerOptions {
  backend?: 'js' | 'interp';
}

export interface HistoricalTailAsyncOptions {
  signal?: AbortSignal;
  budgetMs?: number;
  now?: () => number;
  yieldControl?: () => Promise<void>;
}

export class HistoricalReplayCancelledError extends Error {
  constructor(message = 'Historical replay was cancelled') {
    super(message);
    this.name = 'AbortError';
  }
}

function sameBar(a: Bar, b: Bar): boolean {
  return (
    Object.is(a.time, b.time) &&
    Object.is(a.open, b.open) &&
    Object.is(a.high, b.high) &&
    Object.is(a.low, b.low) &&
    Object.is(a.close, b.close) &&
    Object.is(a.volume, b.volume)
  );
}

function sameBars(a: readonly Bar[], b: readonly Bar[]): boolean {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index++) {
    if (!sameBar(a[index], b[index])) return false;
  }
  return true;
}

function samePrefix(a: readonly Bar[], b: readonly Bar[], length: number): boolean {
  for (let index = 0; index < length; index++) {
    if (!sameBar(a[index], b[index])) return false;
  }
  return true;
}

function sameRange(
  a: readonly Bar[],
  aStart: number,
  b: readonly Bar[],
  bStart: number,
  length: number,
): boolean {
  for (let index = 0; index < length; index++) {
    if (!sameBar(a[aStart + index], b[bStart + index])) return false;
  }
  return true;
}

function sameSecurityBars(
  a: Readonly<Record<string, readonly Bar[]>> | undefined,
  b: Readonly<Record<string, readonly Bar[]>> | undefined,
): boolean {
  const aKeys = Object.keys(a ?? {}).sort();
  const bKeys = Object.keys(b ?? {}).sort();
  if (aKeys.length !== bKeys.length || aKeys.some((key, index) => key !== bKeys[index]))
    return false;
  return aKeys.every((key) => sameBars(a?.[key] ?? [], b?.[key] ?? []));
}

function cloneBars(bars: readonly Bar[]): Bar[] {
  return bars.map((bar) => ({ ...bar }));
}

function cloneSecurityBars(
  bars: Readonly<Record<string, readonly Bar[]>> | undefined,
): Record<string, Bar[]> | undefined {
  if (!bars) return undefined;
  return Object.fromEntries(Object.entries(bars).map(([key, value]) => [key, cloneBars(value)]));
}

function sameSemanticValue(a: unknown, b: unknown, seen = new Map<object, Set<object>>()): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;

  let matches = seen.get(a);
  if (matches?.has(b)) return true;
  if (!matches) {
    matches = new Set<object>();
    seen.set(a, matches);
  }
  matches.add(b);

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => sameSemanticValue(value, b[index], seen));
  }

  const aProto = Object.getPrototypeOf(a);
  const bProto = Object.getPrototypeOf(b);
  const aPlain = aProto === Object.prototype || aProto === null;
  const bPlain = bProto === Object.prototype || bProto === null;
  if (!aPlain || !bPlain) return false;

  const aKeys = Object.keys(a as Record<string, unknown>).sort();
  const bKeys = Object.keys(b as Record<string, unknown>).sort();
  if (aKeys.length !== bKeys.length || aKeys.some((key, index) => key !== bKeys[index])) {
    return false;
  }
  return aKeys.every((key) =>
    sameSemanticValue(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
      seen,
    ),
  );
}

function normalizedMintick(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0.01;
}

const DEFAULT_HISTORICAL_BUDGET_MS = 8;

function defaultNow(): number {
  return globalThis.performance?.now() ?? Date.now();
}

async function defaultYieldControl(): Promise<void> {
  const scheduler = (
    globalThis as typeof globalThis & {
      scheduler?: { yield?: () => Promise<void> };
    }
  ).scheduler;
  if (scheduler?.yield) {
    await scheduler.yield();
    return;
  }
  await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 0));
}

type PreparedSeed = {
  bars: Bar[];
  ctx: ExecutionContext;
  driver: Driver;
  runContext: HistoricalTailRunContext;
};

/**
 * Persistent full-history-compatible execution for the dominant chart case: a
 * changed OHLCV payload at the existing final timestamp. Length changes remain
 * a conservative full seed until dataset-extent builtin hazards are available
 * as compiler metadata.
 */
export class HistoricalTailRunner {
  private ctx: ExecutionContext | null = null;
  private driver: Driver | null = null;
  private bars: Bar[] = [];
  private runContext: HistoricalTailRunContext | null = null;
  private poisoned = false;
  private generation = 0;

  constructor(
    private readonly script: CompiledScript,
    private readonly options: HistoricalTailRunnerOptions = {},
  ) {}

  private contextChangeReason(run: HistoricalTailRunContext): HistoricalTailRunReason | null {
    if ((run.symbol ?? '') !== (this.runContext?.symbol ?? '')) return 'symbol-change';
    if ((run.timeframe ?? '') !== (this.runContext?.timeframe ?? '')) return 'timeframe-change';
    if (!Object.is(normalizedMintick(run.mintick), normalizedMintick(this.runContext?.mintick))) {
      return 'mintick-change';
    }
    if (!sameSemanticValue(run.magnifierData, this.runContext?.magnifierData)) {
      return 'magnifier-data-change';
    }
    if (!sameSemanticValue(run.inputs ?? {}, this.runContext?.inputs ?? {})) {
      return 'inputs-change';
    }
    if (!sameSecurityBars(run.securityBars, this.runContext?.securityBars)) {
      return 'security-bars-change';
    }
    return null;
  }

  private fullReplayReason(bars: readonly Bar[]): HistoricalTailRunReason | null {
    if (bars.length === 0) return 'empty';
    if (bars.length > this.bars.length) {
      if (samePrefix(this.bars, bars, this.bars.length)) return 'append';
      const prepended = bars.length - this.bars.length;
      if (sameRange(this.bars, 0, bars, prepended, this.bars.length)) return 'prepend';
      return 'length-change';
    }
    if (bars.length < this.bars.length) return 'shrink';

    const lastIndex = bars.length - 1;
    if (!samePrefix(this.bars, bars, lastIndex)) return 'historical-change';
    if (!Object.is(this.bars[lastIndex]?.time, bars[lastIndex]?.time)) return 'retime';
    if (this.script.metadata.isStrategy) return 'strategy';
    if (this.script.metadata.securityDependencies.length > 0) return 'request-security';
    return null;
  }

  private prepareSeed(bars: readonly Bar[], run: HistoricalTailRunContext): PreparedSeed {
    const nextBars = cloneBars(bars);
    const ctx = new ExecutionContext();
    ctx.ensureHistorySlots(this.script.metadata.historySlotCount);
    if (this.script.metadata.isStrategy) ctx.configureStrategy(this.script.metadata.strategy ?? {});
    ctx.drawPool.setCaps({
      line: this.script.metadata.maxLinesCount,
      label: this.script.metadata.maxLabelsCount,
      box: this.script.metadata.maxBoxesCount,
      polyline: this.script.metadata.maxPolylinesCount,
    });
    if (run.inputs) ctx.inputOverrides = { ...run.inputs };
    ctx.symbol = run.symbol ?? '';
    ctx.tfStr = run.timeframe ?? '';
    ctx.barMagnifierTargetTimeframe =
      ctx.strategyBroker.active && ctx.strategyBroker.settings.useBarMagnifier
        ? barMagnifierTimeframe(run.timeframe ?? '')
        : undefined;
    ctx.mintick = normalizedMintick(run.mintick);
    ctx.magnifierData = run.magnifierData === undefined ? null : structuredClone(run.magnifierData);
    ctx.allBars = nextBars;
    if (run.securityBars) {
      for (const [symbol, security] of Object.entries(run.securityBars)) {
        ctx.securityBars.set(symbol, cloneBars(security));
      }
    }

    const main = this.options.backend === 'interp' ? this.script.interpret : this.script.main;
    return {
      bars: nextBars,
      ctx,
      driver: new Driver(main, ctx),
      runContext: {
        symbol: run.symbol ?? '',
        timeframe: run.timeframe ?? '',
        inputs: run.inputs ? structuredClone(run.inputs) : undefined,
        securityBars: cloneSecurityBars(run.securityBars),
        mintick: ctx.mintick,
        magnifierData:
          run.magnifierData === undefined ? undefined : structuredClone(run.magnifierData),
      },
    };
  }

  private commitSeed(
    prepared: PreparedSeed,
    reason: HistoricalTailRunReason,
  ): HistoricalTailRunResult {
    this.ctx = prepared.ctx;
    this.driver = prepared.driver;
    this.bars = prepared.bars;
    this.runContext = prepared.runContext;
    this.poisoned = false;
    return { kind: 'full', reason, dirtyFromIndex: 0, structural: true };
  }

  private assertActive(generation: number, signal?: AbortSignal): void {
    if (signal?.aborted || generation !== this.generation) {
      throw new HistoricalReplayCancelledError();
    }
  }

  seed(
    bars: readonly Bar[],
    run: HistoricalTailRunContext = {},
    reason: HistoricalTailRunReason = 'explicit-seed',
  ): HistoricalTailRunResult {
    const generation = ++this.generation;
    const prepared = this.prepareSeed(bars, run);
    prepared.driver.runHistorical(prepared.bars);
    this.assertActive(generation);
    return this.commitSeed(prepared, reason);
  }

  async seedAsync(
    bars: readonly Bar[],
    run: HistoricalTailRunContext = {},
    options: HistoricalTailAsyncOptions = {},
  ): Promise<HistoricalTailRunResult> {
    const generation = ++this.generation;
    return this.runSeedAsync(bars, run, options, generation, 'explicit-seed');
  }

  private async runSeedAsync(
    bars: readonly Bar[],
    run: HistoricalTailRunContext,
    options: HistoricalTailAsyncOptions,
    generation: number,
    reason: HistoricalTailRunReason,
  ): Promise<HistoricalTailRunResult> {
    this.assertActive(generation, options.signal);
    const prepared = this.prepareSeed(bars, run);
    const budgetMs = Math.max(0, options.budgetMs ?? DEFAULT_HISTORICAL_BUDGET_MS);
    const now = options.now ?? defaultNow;
    const yieldControl = options.yieldControl ?? defaultYieldControl;
    let sliceStarted = now();
    let completed = 0;

    try {
      prepared.driver.prepareHistorical(prepared.bars);
      while (prepared.driver.stepHistorical()) {
        completed += 1;
        this.assertActive(generation, options.signal);
        if (completed < prepared.bars.length && now() - sliceStarted >= budgetMs) {
          await yieldControl();
          this.assertActive(generation, options.signal);
          sliceStarted = now();
        }
      }
      prepared.driver.discardHistoricalPreparation();
      this.assertActive(generation, options.signal);
      return this.commitSeed(prepared, reason);
    } catch (error) {
      prepared.driver.discardHistoricalPreparation();
      throw error;
    }
  }

  reconcile(bars: readonly Bar[], run: HistoricalTailRunContext = {}): HistoricalTailRunResult {
    this.generation += 1;
    if (this.poisoned) return this.seed(bars, run, 'incremental-state-poisoned');
    if (!this.ctx || !this.driver || !this.runContext) {
      return this.seed(bars, run, 'incremental-state-unavailable');
    }

    const contextReason = this.contextChangeReason(run);
    if (contextReason) return this.seed(bars, run, contextReason);
    if (sameBars(this.bars, bars)) {
      return {
        kind: 'unchanged',
        reason: 'unchanged',
        dirtyFromIndex: bars.length,
        structural: false,
      };
    }

    // Initial conservative capability: indicators without request.security may
    // replay only the existing final timestamp. Appends and all earlier changes
    // use a fresh full run.
    const fullReason = this.fullReplayReason(bars);
    if (fullReason) return this.seed(bars, run, fullReason);

    const lastIndex = bars.length - 1;
    const nextBars = cloneBars(bars);
    this.ctx.allBars = nextBars;
    try {
      if (!this.driver.replayHistoricalTail(nextBars, lastIndex)) {
        return this.seed(bars, run, 'tail-replay-unavailable');
      }
    } catch (error) {
      this.poisoned = !this.recoverCommittedTail(lastIndex);
      throw error;
    }
    this.bars = nextBars;
    return { kind: 'suffix', reason: 'tail-replay', dirtyFromIndex: lastIndex, structural: false };
  }

  async reconcileAsync(
    bars: readonly Bar[],
    run: HistoricalTailRunContext = {},
    options: HistoricalTailAsyncOptions = {},
  ): Promise<HistoricalTailRunResult> {
    const generation = ++this.generation;
    this.assertActive(generation, options.signal);
    if (this.poisoned) {
      return this.runSeedAsync(bars, run, options, generation, 'incremental-state-poisoned');
    }
    if (!this.ctx || !this.driver || !this.runContext) {
      return this.runSeedAsync(bars, run, options, generation, 'incremental-state-unavailable');
    }

    const contextReason = this.contextChangeReason(run);
    if (contextReason) return this.runSeedAsync(bars, run, options, generation, contextReason);
    if (sameBars(this.bars, bars)) {
      return {
        kind: 'unchanged',
        reason: 'unchanged',
        dirtyFromIndex: bars.length,
        structural: false,
      };
    }

    const fullReason = this.fullReplayReason(bars);
    if (fullReason) return this.runSeedAsync(bars, run, options, generation, fullReason);

    this.assertActive(generation, options.signal);
    const lastIndex = bars.length - 1;
    const nextBars = cloneBars(bars);
    this.ctx.allBars = nextBars;
    try {
      if (!this.driver.replayHistoricalTail(nextBars, lastIndex)) {
        return this.runSeedAsync(bars, run, options, generation, 'tail-replay-unavailable');
      }
    } catch (error) {
      this.poisoned = !this.recoverCommittedTail(lastIndex);
      throw error;
    }
    this.assertActive(generation, options.signal);
    this.bars = nextBars;
    return { kind: 'suffix', reason: 'tail-replay', dirtyFromIndex: lastIndex, structural: false };
  }

  private recoverCommittedTail(lastIndex: number): boolean {
    if (!this.ctx || !this.driver) return false;
    try {
      this.ctx.allBars = this.bars;
      return this.driver.replayHistoricalTail(this.bars, lastIndex);
    } catch {
      return false;
    }
  }

  reset(): void {
    this.generation += 1;
    this.ctx = null;
    this.driver = null;
    this.bars = [];
    this.runContext = null;
    this.poisoned = false;
  }

  get context(): ExecutionContext {
    if (!this.ctx) throw new Error('HistoricalTailRunner has not been seeded');
    return this.ctx;
  }

  get outputs(): OutputCollector {
    return this.context.out;
  }

  get drawings(): readonly DrawObject[] {
    return this.context.drawings;
  }
}
